import { apiClient } from '@/api/client';
import type {
  AnalyticsFilters,
  UsageMetricsSummary,
  CurationMetricsSummary,
  ExtractionOutputMetrics,
  ExtractionHealthMetrics,
  PageOfExtractionEventListItem,
  SubstanceVerificationMetrics,
  FieldChangeDetailResponse,
  FieldValueChangeResponse,
} from '@/api/types';
import type { CurationChangeKind } from '@/api/generated-types';

export async function getUsageMetrics(filters: AnalyticsFilters): Promise<UsageMetricsSummary> {
  const { data } = await apiClient.get('/api/v1/analytics/usage', {
    params: {
      From: filters.from,
      To: filters.to,
      TeamId: filters.teamId,
      GroupBy: filters.groupBy,
      DocumentType: filters.documentType,
    },
  });
  return data;
}

export async function getCurationMetrics(
  filters: AnalyticsFilters
): Promise<CurationMetricsSummary> {
  const { data } = await apiClient.get('/api/v1/analytics/curation', {
    params: {
      From: filters.from,
      To: filters.to,
      TeamId: filters.teamId,
      GroupBy: filters.groupBy,
      DocumentType: filters.documentType,
    },
  });
  return data;
}

export async function getExtractionOutputMetrics(
  filters: AnalyticsFilters
): Promise<ExtractionOutputMetrics> {
  const { data } = await apiClient.get('/api/v1/analytics/extraction-output', {
    params: {
      From: filters.from,
      To: filters.to,
      TeamId: filters.teamId,
      GroupBy: filters.groupBy,
      DocumentType: filters.documentType,
    },
  });
  return data;
}

export async function getSubstanceVerificationMetrics(
  filters: AnalyticsFilters
): Promise<SubstanceVerificationMetrics> {
  const { data } = await apiClient.get('/api/v1/analytics/substance-validation', {
    params: {
      From: filters.from,
      To: filters.to,
      TeamId: filters.teamId,
      GroupBy: filters.groupBy,
      DocumentType: filters.documentType,
    },
  });
  return data;
}

export async function getExtractionHealthMetrics(
  filters: AnalyticsFilters
): Promise<ExtractionHealthMetrics> {
  const { data } = await apiClient.get('/api/v1/analytics/extraction-health', {
    params: {
      From: filters.from,
      To: filters.to,
      TeamId: filters.teamId,
      GroupBy: filters.groupBy,
      DocumentType: filters.documentType,
    },
  });
  return data;
}

export async function getExtractionEvents(
  filters: AnalyticsFilters,
  eventType?: string | null,
  messageFilter?: string | null,
  page: number = 1,
  pageSize: number = 20
): Promise<PageOfExtractionEventListItem> {
  const { data } = await apiClient.get('/api/v1/analytics/extraction-health/events', {
    params: {
      From: filters.from,
      To: filters.to,
      TeamId: filters.teamId,
      DocumentType: filters.documentType,
      EventType: eventType ?? undefined,
      MessageFilter: messageFilter ?? undefined,
      Page: page,
      PageSize: pageSize,
    },
  });
  return data;
}

export async function getFieldChangeDetails(
  fieldKey: string,
  filters: AnalyticsFilters,
  page: number = 1,
  pageSize: number = 20
): Promise<FieldChangeDetailResponse> {
  const { data } = await apiClient.get('/api/v1/analytics/curation/field-changes', {
    params: {
      FieldKey: fieldKey,
      From: filters.from,
      To: filters.to,
      TeamId: filters.teamId,
      DocumentType: filters.documentType,
      Page: page,
      PageSize: pageSize,
    },
  });
  return data;
}

export async function getFieldValueChanges(
  fieldKey: string,
  filters: AnalyticsFilters,
  changeKind?: CurationChangeKind
): Promise<FieldValueChangeResponse> {
  const { data } = await apiClient.get('/api/v1/analytics/curation/field-value-changes', {
    params: {
      FieldKey: fieldKey,
      From: filters.from,
      To: filters.to,
      TeamId: filters.teamId,
      DocumentType: filters.documentType,
      ChangeKind: changeKind,
    },
  });
  return data;
}
